import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { buildSignupUrl } from "@/lib/constants";
import { trackEvent } from "@/lib/analytics";
import { getAdVariantFromUrl } from "@/lib/adVariants";
// RESONATE_ANALYTICS_INSTRUMENTED
// RESONATE_PERF_V3 — framer-motion scroll parallax removed: useScroll forced
// synchronous layout reads (93ms+ reflows) during LCP paint and kept ~40KB gz
// of framer-motion in the critical bundle. Orbs keep their idle CSS float.
// Above-fold elements (h1, subhead, CTA, guarantee, trust line) render
// statically — no entry animation — so they match the static hero shell in
// index.html and the React mount swap is invisible. Keep them in sync with
// that shell.
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { TrialGuarantee } from "@/components/site/TrialGuarantee";

export const Hero = () => {
  // Message-match: if this visit came from a Reddit ad, swap the hero copy
  // to echo the ad headline the user just clicked.
  const location = useLocation();
  const variant = useMemo(
    () => getAdVariantFromUrl("founder"),
    [location.pathname, location.search, location.hash]
  );
  useEffect(() => {
    if (variant) trackEvent("ad_variant_shown", { slug: variant.slug, persona: "founder" });
  }, [variant]);

  return (
    <section
      data-testid="hero-section"
      className="relative pt-24 pb-16 md:pt-40 md:pb-32 overflow-hidden"
    >
      {/* grid backdrop - hidden on mobile to reduce paint */}
      <div
        className="absolute inset-0 opacity-[0.4] pointer-events-none hidden md:block"
        style={{
          backgroundImage:
            "linear-gradient(rgba(38,31,73,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(38,31,73,0.06) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse 80% 60% at 50% 30%, black 50%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 30%, black 50%, transparent 100%)",
        }}
      />

      {/* orbs — hidden on mobile (blur-3xl is expensive) */}
      <div
        className="orb-float absolute -top-20 -left-32 w-[480px] h-[480px] rounded-full opacity-50 blur-3xl pointer-events-none hidden md:block"
        aria-hidden
      >
        <div className="w-full h-full rounded-full" style={{ background: "radial-gradient(circle, rgba(38,31,73,0.35), transparent 65%)" }} />
      </div>
      <div
        className="orb-float absolute top-40 -right-40 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl pointer-events-none hidden md:block"
        aria-hidden
      >
        <div className="w-full h-full rounded-full" style={{ background: "radial-gradient(circle, rgba(52,34,85,0.35), transparent 65%)" }} />
      </div>
      <div
        className="orb-float absolute bottom-0 left-1/3 w-[360px] h-[360px] rounded-full opacity-30 blur-3xl pointer-events-none hidden md:block"
        aria-hidden
      >
        <div className="w-full h-full rounded-full" style={{ background: "radial-gradient(circle, rgba(38,31,73,0.4), transparent 65%)" }} />
      </div>

      <div className="relative max-w-[1100px] mx-auto px-6 text-center">
        {/* LCP element - no Reveal wrapper so it paints immediately. */}
        <h1
          data-testid="hero-headline"
          data-variant={variant?.slug || "default"}
          style={{ fontFamily: "var(--f-serif)", fontWeight: 400 }}
          className="text-[36px] sm:text-[52px] md:text-[clamp(48px,6.5vw,84px)] leading-[1.04] tracking-[-0.04em] text-[#111827] mb-4 md:mb-6"
        >
          {(variant?.headline || ["Turning founders into", "LinkedIn influencers in 30 days"]).map((line, i, arr) => {
            const isLast = i === arr.length - 1;
            const Tag = isLast ? "em" : "span";
            return (
              <Tag key={i} className="block">
                {line}
              </Tag>
            );
          })}
        </h1>

        <p data-testid="hero-subhead" className="max-w-[680px] mx-auto text-[15px] md:text-[18px] text-[rgba(17,24,39,0.72)] mb-6 md:mb-10 leading-snug md:leading-relaxed">
          {variant?.subhead || "Resonate turns what you're actually doing - in Notion, Slack, GitHub, HubSpot - into posts that sound exactly like you. No generic AI. No ban risk. Just results."}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
            <a
              href={buildSignupUrl(variant ? `ad_${variant.slug}` : 'hero')} onClick={() => trackEvent('cta_click', { location: 'hero', variant: variant?.slug || null })}
              data-testid="hero-cta-primary"
              aria-label={variant?.ctaPrimary ? `${variant.ctaPrimary} (hero)` : "Start for free (hero)"}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#111827] hover:bg-[#261F49] text-white px-7 py-4 md:py-3.5 rounded-xl font-medium text-[15px] transition-colors group min-h-[52px]"
            >
              {variant?.ctaPrimary || "Start for free"}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })}
              className="hidden md:inline-flex items-center gap-2 bg-transparent text-[rgba(17,24,39,0.78)] px-6 py-3.5 rounded-xl font-normal text-[15px] border border-[rgba(17,24,39,0.18)] hover:border-[#261F49] hover:text-[#261F49] transition-colors"
            >
              {variant?.ctaSecondary || "See how it works"}
            </button>
        </div>

        {/* Static (no Reveal) — part of the above-fold shell-synced block. */}
        <TrialGuarantee variant="compact" align="center" />

        <p className="text-[12px] md:text-[12.5px] text-[rgba(17,24,39,0.72)] mt-3 md:mt-4">
          Free 14 days · Google one-click signup · Cancel anytime
        </p>

        {/* Opportunity-cost stats strip - homepage. Three stats only
            (75% / 9 in 10 / 60%). Sources attribution is a small caption
            beneath - hover-only hyperlinks were removed per user directive. */}
        <StaggerGroup data-testid="hero-stats-strip" className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 mt-16 md:mt-20 max-w-[860px] mx-auto" stagger={0.1}>
          {[
            ["75%", "of decision-makers researched a product after one piece of thought leadership"],
            ["9 in 10", "decision-makers are more receptive to outreach from people who publish consistently"],
            ["60%", "of B2B buyers pay a premium to work with a recognised thought leader"],
          ].map(([n, l]) => (
            <StaggerItem key={n} variant="blur" className="text-center md:text-left">
              <div style={{ fontFamily: "var(--f-serif)", fontWeight: 400 }} className="text-[38px] md:text-[42px] tracking-[-0.04em] text-[#111827] leading-none mb-2">
                {n}
              </div>
              <div className="text-[12.5px] md:text-[13px] text-[rgba(17,24,39,0.55)] leading-snug max-w-[240px] mx-auto md:mx-0">
                {l}
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>

        <Reveal delay={0.2}>
          <p data-testid="hero-stats-sources" className="mt-6 text-[9.5px] uppercase tracking-[0.12em] text-[rgba(17,24,39,0.4)] max-w-[640px] mx-auto">
            Sources: Edelman-LinkedIn B2B Thought Leadership Impact Report · Just Connecting Algorithm Insights
          </p>
        </Reveal>
      </div>
    </section>
  );
};
